import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getWorkspaceDomainsWithNs, getDomainInfoJson, updateDomainContacts, modifyDomainSettings, updateDomainAutoRenew, updateNameservers, getWhoisPrivacyState, updateWhoisPrivacy, addUserDomain, connectUserDomainToProject, checkUserDomainStatus, listUserDomains, addDomainWithNs, checkDomainWithNs, deleteDomainWithNs, disconnectDomainWithNsFromProject, getThirdPartyDomainDetails, disconnectThirdPartyDomain, revokeDomain, deleteThirdPartyDomain } from '../api';
import { showSimpleSuccess, showSimpleError } from '@/lib/toast.jsx';

// React Query hook to get user domains
export const useWorkspaceDomainsWithNs = (workspaceId, options = {}) => {
  return useQuery({
    queryKey: ['workspaceDomainsWithNs', workspaceId],
    queryFn: async () => {
      console.log('🔍 useWorkspaceDomainsWithNs - Fetching for workspaceId:', workspaceId);
      const result = await getWorkspaceDomainsWithNs(workspaceId);
      console.log('🔍 useWorkspaceDomainsWithNs - Result:', result);
      console.log('🔍 useWorkspaceDomainsWithNs - Number of domains:', result?.data?.length || 0);
      return result;
    },
    enabled: !!workspaceId && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options,
  })
}

// // React Query hook to get domain detailed info
// export const useDomainInfo = (domain, options = {}) => {
//   return useQuery({
//     queryKey: ['domainInfo', domain],
//     queryFn: () => getDomainInfo(domain),
//     enabled: !!domain && options.enabled !== false,
//     staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
//     cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
//     ...options,
//   });
// };

// React Query hook to get detailed domain info (with contacts, nameservers, etc.)
export const useDomainInfoJson = (domain, options = {}) => {
  return useQuery({
    queryKey: ['domainInfoJson', domain],
    queryFn: async () => {
      // console.log('🔍 useDomainInfoJson - Fetching data for domain:', domain);
      const result = await getDomainInfoJson(domain);
      // console.log('🔍 useDomainInfoJson - Received data:', result);
      // console.log('🔍 useDomainInfoJson - whoisPrivacyState in response:', result?.data?.whoisPrivacyState);
      // console.log('🔍 useDomainInfoJson - Fetch time:', new Date().toISOString());
      return result;
    },
    enabled: !!domain && options.enabled !== false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    ...options,
  });
};

// React Query mutation hook to update domain contacts
export const useUpdateDomainContacts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain, contactData }) => updateDomainContacts(domain, contactData),
    onSuccess: (data, variables) => {
      // Invalidate and refetch the domain info to get updated data
      queryClient.invalidateQueries(['domainInfo', variables.domain]);
      queryClient.invalidateQueries(['domainInfoJson', variables.domain]);
      queryClient.invalidateQueries(['userDomains']);

      // Show success toast
      showSimpleSuccess(`Domain contacts updated successfully for ${variables.domain}`);
    },
    onError: (error, variables) => {
      showSimpleError(`Failed to update contacts for ${variables.domain}: ${error.message}`);
    },
  });
};

// React Query mutation hook to modify domain settings
export const useModifyDomainSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain, settings }) => modifyDomainSettings(domain, settings),
    onMutate: async ({ domain, settings }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(['domainInfoJson', domain]);

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['domainInfoJson', domain]);


      // Optimistically update the cache for auto_renew
      if (previousData && settings.auto_renew !== undefined) {
        const optimisticData = {
          ...previousData,
          data: {
            ...previousData.data,
            autoRenew: settings.auto_renew === "1"
          }
        };

        queryClient.setQueryData(['domainInfoJson', domain], optimisticData);
      }

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onSuccess: async (data, variables) => {
      // Update cache with the actual server data
      const currentData = queryClient.getQueryData(['domainInfoJson', variables.domain]);
      if (currentData && data?.data) {
        const updatedData = {
          ...currentData,
          data: {
            ...currentData.data,
            ...data.data
          }
        };
        queryClient.setQueryData(['domainInfoJson', variables.domain], updatedData);
      }

      // Still invalidate to ensure we're in sync
      queryClient.invalidateQueries(['domainInfo', variables.domain]);
      queryClient.invalidateQueries(['domainInfoJson', variables.domain]);
      queryClient.invalidateQueries(['userDomains']);

      // Show success toast
      showSimpleSuccess(`Domain settings updated successfully for ${variables.domain}`);
    },
    onError: (error, variables, context) => {
      // Show error toast
      showSimpleError(`Failed to update settings for ${variables.domain}: ${error.message}`);

      // If the mutation fails, use the context to roll back
      if (context?.previousData) {
        console.log('🔄 Rolling back to previous data due to error');
        queryClient.setQueryData(['domainInfoJson', variables.domain], context.previousData);
      }
    },
  });
};

// React Query mutation hook to update domain auto-renew
export const useUpdateDomainAutoRenew = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain, autoRenew }) => updateDomainAutoRenew(domain, autoRenew),
    onSuccess: (data, variables) => {
      console.log('✅ Auto-renew update response:', data);
      console.log('✅ Variables:', variables);

      // Invalidate and refetch the domain info to get updated data
      queryClient.invalidateQueries(['domainInfo', variables.domain]);
      queryClient.invalidateQueries(['domainInfoJson', variables.domain]);
      queryClient.invalidateQueries(['userDomains']);
      queryClient.invalidateQueries(['workspaceDomainsWithNs']);

      // Show success toast
      const status = variables.autoRenew ? 'enabled' : 'disabled';
      showSimpleSuccess(`Auto-renewal ${status} successfully for ${variables.domain}`);
    },
    onError: (error, variables) => {
      console.error('❌ Failed to update auto-renewal:', error);
      showSimpleError(`Failed to update auto-renewal for ${variables.domain}: ${error.message}`);
    },
  });
};

// React Query mutation hook to update nameservers
export const useUpdateNameservers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain, nameservers }) => updateNameservers(domain, nameservers),
    onSuccess: (data, variables) => {
      // Invalidate and refetch the domain info to get updated data
      queryClient.invalidateQueries(['domainInfo', variables.domain]);
      queryClient.invalidateQueries(['domainInfoJson', variables.domain]);
      queryClient.invalidateQueries(['userDomains']);

      // Show success toast
      showSimpleSuccess(`Nameservers updated successfully for ${variables.domain}`);
    },
    onError: (error, variables) => {
      console.error('Failed to update nameservers:', error);
      showSimpleError(`Failed to update nameservers for ${variables.domain}: ${error.message}`);
    },
  });
};

// React Query hook to get WHOIS privacy state
export const useWhoisPrivacyState = (domain, options = {}) => {
  return useQuery({
    queryKey: ['whoisPrivacyState', domain],
    queryFn: () => getWhoisPrivacyState(domain),
    enabled: !!domain && options.enabled !== false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    ...options,
  });
};

// React Query mutation hook to update WHOIS privacy
export const useUpdateWhoisPrivacy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain, state }) => updateWhoisPrivacy(domain, state),
    onSuccess: (data, variables) => {
      // Invalidate and refetch queries to get updated data
      queryClient.invalidateQueries(['whoisPrivacyState', variables.domain]);
      queryClient.invalidateQueries(['domainInfoJson', variables.domain]);
      queryClient.invalidateQueries(['userDomains']);

      // Show success toast
      const isPrivate = variables.state === 'enable';
      showSimpleSuccess(`WHOIS privacy ${isPrivate ? 'enabled' : 'disabled'} successfully for ${variables.domain}`);
    },
    onError: (error, variables) => {
      showSimpleError(`Failed to update WHOIS privacy for ${variables.domain}: ${error.message}`);
    },
  });
};

// =======================
// Third-party Domains API
// =======================

// List user domains (third-party) by workspace or project
export const useListUserDomains = (userId, params = {}, options = {}) => {
  const { project, workspaceId, verifiedDomainsOnly, offset, limit } = params;

  return useQuery({
    queryKey: ['userDomainsByScope', userId, workspaceId],
    queryFn: () => listUserDomains(userId, { project, workspaceId, verifiedDomainsOnly, offset, limit }),
    enabled: !!userId && !!workspaceId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// Add domain (project optional per updated docs)
export const useAddUserDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, name, project, workspaceId }) => addUserDomain(userId, { name, project, workspaceId }),
    onSuccess: (_resp, vars) => {
      queryClient.invalidateQueries({ queryKey: ['userDomainsByScope', vars.userId] });
    },
  });
};

// Check TXT/A verification status
export const useCheckUserDomainStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, domain }) => checkUserDomainStatus(userId, domain),
    onSuccess: (data, variables) => {
      // Invalidate domain details cache so pages show fresh verification status
      queryClient.invalidateQueries({ queryKey: ['thirdPartyDomainDetails', variables.userId, variables.domain] });
      queryClient.invalidateQueries({ queryKey: ['userDomainsByScope', variables.userId] });
    },
  });
};

// Add domain with nameservers
export const useAddDomainWithNs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domainData, project, workspaceId }) => addDomainWithNs(domainData, project, workspaceId),
    onSuccess: (data, variables) => {
      // Don't show toast here - let the parent component handle it
      // Invalidate and refetch workspace domains
      queryClient.invalidateQueries({ queryKey: ['workspaceDomainsWithNs', variables.workspaceId] });
    },
    onError: (error) => {
      console.error('Failed to add domain with nameservers:', error);
      showSimpleError(`Failed to add domain: ${error.message}`);
    },
  });
};

//check domain status
export const useCheckDomainWithNsStatus = (showToast = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, domain }) => checkDomainWithNs(domain, workspaceId),
    onSuccess: (data, variables) => {
      // Invalidate and refetch workspace domains to get updated status
      queryClient.invalidateQueries({ queryKey: ['workspaceDomainsWithNs', variables.workspaceId] });

      if (showToast) {
        if (data.message?.includes('successfully verified')) {
          showSimpleSuccess(`Domain ${variables.domain} DNS verification successful`);
        } else {
          showSimpleError(`DNS verification failed for ${variables.domain}. Please check your DNS settings.`);
        }
      }
    },
    onError: (error, variables) => {
      console.error('DNS check failed:', error);
      if (showToast) {
        showSimpleError(`DNS verification failed for ${variables.domain}. Please try again.`);
      }
    },
  });
};

// Delete domain with nameservers
export const useDeleteDomainWithNs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, domain }) => deleteDomainWithNs(domain, workspaceId),
    onSuccess: (data, variables) => {
      // Invalidate and refetch workspace domains to remove deleted domain
      queryClient.invalidateQueries({ queryKey: ['workspaceDomainsWithNs', variables.workspaceId] });

      // Show success message
      const message = data?.message || `Domain ${variables.domain} has been deleted successfully`;
      showSimpleSuccess(message);
    },
    onError: (error, variables) => {
      console.error('Delete domain failed:', error);
      // Extract error message more safely
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      showSimpleError(`Failed to delete domain ${variables.domain}: ${errorMessage}`);
    },
  });
};

// Connect verified third-party domain to project
export const useConnectUserDomainToProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, domain, project }) => connectUserDomainToProject(userId, domain, project),
    onSuccess: (data, variables) => {
      // Invalidate and refetch user domains to get updated project connection
      queryClient.invalidateQueries({ queryKey: ['userDomainsByScope', variables.userId] });

      // Don't show success message automatically - let the parent component handle it
      // The success popup will be shown by DomainTable only if no error occurs
    },
    onError: (error, variables) => {
      console.error('Connect domain to project failed:', error);
      // Extract error message more safely
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      showSimpleError(`Failed to connect domain ${variables.domain} to project: ${errorMessage}`);
    },
  });
};

// Disconnect withNS domain from project
export const useDisconnectDomainWithNsFromProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, domain }) => disconnectDomainWithNsFromProject(workspaceId, domain),
    onSuccess: (data, variables) => {
      // Invalidate and refetch workspace domains to get updated project connection
      queryClient.invalidateQueries({ queryKey: ['workspaceDomainsWithNs', variables.workspaceId] });

      // Show success message
      const message = data?.message || `Domain ${variables.domain} disconnected from project successfully`;
      showSimpleSuccess(message);
    },
    onError: (error, variables) => {
      console.error('Disconnect withNS domain from project failed:', error);
      // Extract error message more safely
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      showSimpleError(`Failed to disconnect domain ${variables.domain} from project: ${errorMessage}`);
    },
  });
};

// Get third-party domain details
export const useGetThirdPartyDomainDetails = (userId, domain, options = {}) => {
  return useQuery({
    queryKey: ['thirdPartyDomainDetails', userId, domain],
    queryFn: () => getThirdPartyDomainDetails(userId, domain),
    enabled: !!userId && !!domain && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// Disconnect third-party domain from project
export const useDisconnectThirdPartyDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, domain }) => disconnectThirdPartyDomain(userId, domain),
    onSuccess: (data, variables) => {
      // Invalidate and refetch user domains to get updated project connection
      queryClient.invalidateQueries({ queryKey: ['userDomainsByScope', variables.userId] });

      // Show success message
      const message = data?.message || `Domain ${variables.domain} disconnected from project successfully`;
      showSimpleSuccess(message);
    },
    onError: (error, variables) => {
      console.error('Disconnect third-party domain from project failed:', error);
      // Extract error message more safely
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      showSimpleError(`Failed to disconnect domain ${variables.domain} from project: ${errorMessage}`);
    },
  });
};

// Revoke domain (withNS domains only)
export const useRevokeDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain, notes = '' }) => revokeDomain(domain, notes),
    onSuccess: (data, variables) => {
      // Invalidate and refetch workspace domains to remove revoked domain
      queryClient.invalidateQueries({ queryKey: ['workspaceDomainsWithNs'] });

      // Show success message with refund info if available
      const refundAmount = data?.total_refund ? ` Refund: $${data.total_refund}` : '';
      const message = data?.responseText || `Domain ${variables.domain} revoked successfully${refundAmount}`;
      showSimpleSuccess(message);
    },
    onError: (error, variables) => {
      console.error('Revoke domain failed:', error);
      // Extract error message more safely
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      showSimpleError(`Failed to revoke domain ${variables.domain}: ${errorMessage}`);
    },
  });
};

// Delete third-party domain
export const useDeleteThirdPartyDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, domain }) => deleteThirdPartyDomain(userId, domain),
    onSuccess: (data, variables) => {
      // Invalidate and refetch user domains to remove deleted domain
      queryClient.invalidateQueries({ queryKey: ['userDomainsByScope', variables.userId] });

      // Show success message
      const message = data?.message || `Domain ${variables.domain} has been deleted successfully`;
      showSimpleSuccess(message);
    },
    onError: (error, variables) => {
      console.error('Delete third-party domain failed:', error);
      // Extract error message more safely
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      showSimpleError(`Failed to delete domain ${variables.domain}: ${errorMessage}`);
    },
  });
};

